import React, { FC } from 'react';
import { isPhone } from '@helpers';
import Images from '@components/machines/components/detailsComponents/activeMachine/components/Images';
import { DOCUMENT_ENTITY_TYPE } from '@components/imageHandling/common';
import ColorWrapper from '@styles/colors';
import { DimensionValue, ScrollView, StyleSheet, View } from 'react-native';
import { scale as initialScale } from '@components/frame';
import { IMAGES_PANEL_VIEW_MODE_WIDTH, SCREEN_BORDER_RADIUS } from '@v2/pages/common/constants/base.constants';
import { ScaleFunction } from '@components/operatorPanel/interfaces';
import { font as initialFont } from '@styles/fonts';
import { ColorFunction } from '@styles/colors/types';
import { useManageImagesContainer } from './hooks/useManageImagesContainer';
import { ScopeId } from '@interfaces/base';

export enum ImagesContainerMode {
  ROW = 'row',
  COLUMN = 'column',
  DEFAULT = 'default',
}

export interface ImagesContainerProps {
  C: ColorFunction;
  mode: ImagesContainerMode;
  panelViewMode: boolean;
  maxDetailsWidth?: DimensionValue;
  isImageHandlingNavigation?: boolean;
  clearState?: any;
  onlySelectImage?: boolean;
  uploadImage?: any;
  largeAddView?: any;
  setGlobalImageContext?: any;
  flatLineBorderRadiusConfig?: {
    borderBottomLeftRadius: number;
    borderBottomRightRadius: number;
  };
  containerBorderRadiusConfig?: {
    borderBottomLeftRadius: number;
    borderBottomRightRadius: number;
  };
  noBorder?: boolean;
  bg?: string;
  documentRetrieveKey?: (typeof DOCUMENT_ENTITY_TYPE)[keyof typeof DOCUMENT_ENTITY_TYPE];
  documentRetrieveId?: number | null;
  retrieveAllDocuments?: boolean;
  machineId?: number;
  onImagePress?: (selectedImageKey: string) => void;
  scale?: ScaleFunction;
  font?: typeof initialFont;
  preselectedScopeIds?: ScopeId[]; 
}

const ImagesContainer: FC<ImagesContainerProps> = (props) => {
  const {
    C,
    mode,
    maxDetailsWidth,
    containerBorderRadiusConfig,
    noBorder,
    panelViewMode,
    documentRetrieveKey,
    documentRetrieveId,
    retrieveAllDocuments,
    machineId,
    scale = initialScale,
    font = initialFont,
    flatLineBorderRadiusConfig,
    bg = 'Background/Light/Field',
  } = props;

  const { imagesDocuments } = useManageImagesContainer({ panelViewMode, documentRetrieveId, documentRetrieveKey, retrieveAllDocuments });

  const imagesProps = {
    ...props,
    machineId,
    flatLineBorderRadiusConfig,
    documentRetrieveKey,
    documentRetrieveId,
    mode,
    images: imagesDocuments,
    panelViewMode: !isPhone && props.panelViewMode,
    scale,
    font,
  };

  const borderRadiusConfig = {
    borderTopLeftRadius: scale(SCREEN_BORDER_RADIUS),
    borderTopRightRadius: scale(SCREEN_BORDER_RADIUS),
    ...containerBorderRadiusConfig,
  };

  if (mode === ImagesContainerMode.DEFAULT) {
    return <Images {...imagesProps} />;
  }

  if (mode === ImagesContainerMode.ROW) {
    return (
      <View
        style={[
          {
            maxWidth: maxDetailsWidth,
            marginInline: 'auto',
            backgroundColor: C(bg),
            width: '100%',
          },
          borderRadiusConfig,
          !noBorder && { borderBottomColor: C('Hairline/Light/No trans'), borderBottomWidth: StyleSheet.hairlineWidth },
        ]}
      >
        <Images {...imagesProps} bg={bg} />
      </View>
    );
  }

  if (mode === ImagesContainerMode.COLUMN) {
    return (
      <ScrollView
        style={{ width: IMAGES_PANEL_VIEW_MODE_WIDTH, maxWidth: IMAGES_PANEL_VIEW_MODE_WIDTH }}
        showsVerticalScrollIndicator={false}
        contentContainerStyle={{ width: IMAGES_PANEL_VIEW_MODE_WIDTH, paddingLeft: scale(18), height: '100%' }}
      >
        <Images {...imagesProps} shouldDisplayFavorites />
      </ScrollView>
    );
  }

  return <Images {...imagesProps} />;
};

export default ColorWrapper(ImagesContainer);
